/**
 * Serviço de autenticação para Supabase
 * Implementa a mesma interface do serviço de autenticação do backend
 */

import { supabase } from '@/integrations/supabase/client';
import type { User, Session, AuthError } from '@supabase/supabase-js';

export interface LoginRequest {
  username: string; // Nome de usuário para login
  password: string;
}

export interface LoginResponse {
  user: User;
  session: Session;
  access_token: string;
  refresh_token: string;
}

export interface RegisterRequest {
  email: string;
  username: string; // Nome de usuário único
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  tenantId?: string;
}

export interface RegisterResponse {
  user: User;
  session: Session | null;
  access_token: string;
  refresh_token: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  user: User;
}

export interface UserProfile {
  id: string;
  email: string;
  username: string; // Nome de usuário único
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  tenantId?: string;
  isSuperadmin?: boolean;
  createdAt: string;
  updatedAt: string;
}

class SupabaseAuthService {
  /**
   * Realiza login do usuário usando username
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Resolve somente o e-mail sem conceder acesso anônimo à tabela profiles.
    const { data: resolvedEmail, error: resolveError } = await (
      supabase.rpc as unknown as (
        fn: string,
        args: { p_identifier: string },
      ) => Promise<{ data: string | null; error: Error | null }>
    )('resolve_login_email', {
      p_identifier: credentials.username.trim(),
    });

    if (resolveError || !resolvedEmail) {
      throw new Error('Usuário não encontrado ou credenciais inválidas');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: resolvedEmail,
      password: credentials.password,
    });

    if (error) {
      throw new Error(this.mapAuthError(error));
    }

    if (!data.user || !data.session) {
      throw new Error('Falha no login: dados de sessão não encontrados');
    }

    // O status só pode ser consultado depois da autenticação.
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('status, deleted_at')
      .eq('id', data.user.id)
      .single();

    if (profileError || !profile) {
      await supabase.auth.signOut();
      throw new Error('Não foi possível carregar o perfil do usuário');
    }

    if (profile.status === 'INACTIVE' || profile.deleted_at) {
      await supabase.auth.signOut();
      throw new Error('Sua conta foi desativada. Entre em contato com o administrador.');
    }

    return {
      user: data.user,
      session: data.session,
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    };
  }

  /**
   * Registra um novo usuário
   */
  async register(userData: RegisterRequest & { tenant_id?: string | number }): Promise<RegisterResponse> {
    try {
      const resolvedTenantId = userData.tenantId ?? userData.tenant_id;
      const tenantIdStr =
        resolvedTenantId != null && String(resolvedTenantId).trim() !== ''
          ? String(resolvedTenantId)
          : null;
      const tenantIdNum = tenantIdStr && /^\d+$/.test(tenantIdStr) ? Number(tenantIdStr) : null;

      // Verificar se email já existe
      const emailExists = await this.checkEmailExists(userData.email);
      if (emailExists) {
        throw new Error('Email já está em uso');
      }

      const preparedPayload: RegisterRequest = {
        ...userData,
        tenantId: tenantIdStr ?? undefined,
      };

      // Preparar dados do registro (detecta primeiro usuário do sistema)
      const preparedData = await this.prepareUserRegistration(preparedPayload);

      if (!preparedData.isFirstUser && !tenantIdNum) {
        throw new Error('Selecione a empresa à qual você está associado');
      }

      // Registrar usuário no Supabase Auth (tenant_id no metadata alimenta o trigger)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            username: userData.username,
            tenant_id: preparedData.isFirstUser ? null : tenantIdStr,
          }
        }
      });

      if (authError) {
        throw new Error(this.mapAuthError(authError));
      }

      if (!authData.user) {
        throw new Error('Falha no registro: usuário não criado');
      }

      let existingProfile: { id: string; tenant_id: number | null } | null = null;
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, tenant_id')
          .eq('id', authData.user.id)
          .maybeSingle();

        if (profile) {
          existingProfile = profile;
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 250));
      }

      if (!existingProfile) {
        if (!authData.session || preparedData.isFirstUser || !tenantIdNum) {
          throw new Error('Cadastro incompleto: perfil não criado. Tente novamente.');
        }

        const { data: createResult, error: createError } = await supabase.rpc('inserir_perfil_usuario', {
          p_email: userData.email,
          p_username: userData.username,
          p_id: authData.user.id,
          p_is_superadmin: false,
          p_role: 'USER',
          p_status: 'PENDING',
          p_tenant_id: tenantIdNum,
          p_first_name: userData.firstName,
          p_last_name: userData.lastName
        }) as { data: any; error: any };

        if (createError || !createResult?.sucesso) {
          throw new Error(
            createError?.message || createResult?.mensagem || 'Cadastro incompleto: falha ao criar perfil'
          );
        }
      } else if (!preparedData.isFirstUser && !existingProfile.tenant_id) {
        throw new Error('Cadastro incompleto: empresa não vinculada. Tente novamente.');
      }

      return {
        user: authData.user,
        session: authData.session,
        access_token: authData.session?.access_token || '',
        refresh_token: authData.session?.refresh_token || '',
      };
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  }

  /**
   * Renova o token de acesso
   */
  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: request.refresh_token,
    });

    if (error) {
      throw new Error(this.mapAuthError(error));
    }

    if (!data.user || !data.session) {
      throw new Error('Falha na renovação do token: dados de sessão não encontrados');
    }

    return {
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
      user: data.user,
    };
  }

  /**
   * Obtém o perfil do usuário atual
   */
  async getCurrentUser(): Promise<UserProfile | null> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Busca o perfil do usuário
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) {
      console.error('Erro ao buscar perfil:', error);
      return null;
    }

    // Verificar se o usuário está ativo
    if (profile.status === 'INACTIVE' || profile.deleted_at) {
      console.log('Usuário inativo detectado, fazendo logout...');
      await supabase.auth.signOut();
      return null;
    }

    return {
      id: profile.id,
      email: user.email || '',
      username: profile.username || '',
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      role: profile.role || 'user',
      status: profile.status || 'PENDING',
      tenantId: profile.tenant_id?.toString() || undefined,
      isSuperadmin: profile.is_superadmin || false,
      createdAt: profile.created_at || '',
      updatedAt: profile.updated_at || '',
    };
  }

  /**
   * Realiza logout do usuário
   */
  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(this.mapAuthError(error));
    }
  }

  /**
   * Solicita reset de senha usando username
   */
  async requestPasswordReset(username: string): Promise<void> {
    // Primeiro, buscar o email pelo username OU email
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('email')
      .or(`username.ilike.${username},email.ilike.${username}`);

    if (profileError || !profiles || profiles.length === 0) {
      throw new Error('Usuário não encontrado');
    }

    const profile = profiles[0];

    const { error } = await supabase.auth.resetPasswordForEmail(profile.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new Error(this.mapAuthError(error));
    }
  }

  /**
   * Reseta a senha do usuário
   */
  async resetPassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      throw new Error(this.mapAuthError(error));
    }
  }

  /**
   * Verifica se o usuário está autenticado
   */
  async isAuthenticated(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    return !!user;
  }

  /**
   * Obtém a sessão atual
   */
  async getCurrentSession(): Promise<Session | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  }

  /**
   * Mapeia erros do Supabase para mensagens amigáveis
   */
  private mapAuthError(error: AuthError): string {
    switch (error.message) {
      case 'Invalid login credentials':
        return 'Email ou senha incorretos';
      case 'Email not confirmed':
        return 'Email não confirmado. Verifique sua caixa de entrada';
      case 'User already registered':
        return 'Usuário já cadastrado com este email';
      case 'Password should be at least 6 characters':
        return 'A senha deve ter pelo menos 6 caracteres';
      case 'Invalid email':
        return 'Email inválido';
      case 'Token has expired':
        return 'Sessão expirada. Faça login novamente';
      case 'Invalid refresh token':
        return 'Token de renovação inválido';
      default:
        return error.message || 'Erro de autenticação';
    }
  }

  /**
   * Escuta mudanças no estado de autenticação
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  }

  /**
   * Verifica se email já existe antes do registro
   */
  async checkEmailExists(email: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('verificar_email_existente', {
      p_email: email
    }) as { data: any; error: any };

    if (error) {
      console.error('Erro ao verificar email:', error);
      return false;
    }

    return data?.existe ?? false;
  }

  /**
   * Prepara dados para registro (verifica se é primeiro usuário, etc.)
   */
  async prepareUserRegistration(userData: RegisterRequest): Promise<{
    isFirstUser: boolean;
    role: string;
    status: string;
    message: string;
  }> {
    const { data, error } = await supabase.rpc('preparar_dados_registro_usuario_3params', {
      p_email: userData.email,
      p_role: userData.role || 'USER',
      p_tenant_id: Number(userData.tenantId) || 0
    }) as { data: any; error: any };

    if (error) {
      throw new Error('Erro ao preparar dados: ' + error.message);
    }

    if (!data?.sucesso) {
      throw new Error(data?.erro || 'Falha ao preparar dados');
    }

    return {
      isFirstUser: data.is_first_user,
      role: data.dados_preparados.role,
      status: data.dados_preparados.status,
      message: data.mensagem
    };
  }

  /**
   * Atualiza perfil do usuário atual
   */
  async updateProfile(firstName: string, lastName: string): Promise<UserProfile> {
    const { data, error } = await supabase.rpc('atualizar_perfil_atual', {
      p_first_name: firstName,
      p_last_name: lastName
    }) as { data: any; error: any };

    if (error) {
      throw new Error('Erro ao atualizar perfil: ' + error.message);
    }

    if (!data?.sucesso) {
      throw new Error(data?.erro || 'Falha ao atualizar perfil');
    }

    return {
      id: data.usuario.id,
      email: data.usuario.email,
      username: data.usuario.username || '',
      firstName: data.usuario.first_name,
      lastName: data.usuario.last_name,
      role: data.usuario.role,
      status: data.usuario.status || 'ACTIVE',
      tenantId: data.usuario.tenant_id,
      isSuperadmin: data.usuario.is_superadmin,
      createdAt: data.usuario.created_at,
      updatedAt: data.usuario.updated_at,
    };
  }

  /**
   * Verifica se é o primeiro usuário do sistema
   */
  async isFirstUser(): Promise<boolean> {
    const { data, error } = await supabase.rpc('verificar_primeiro_usuario') as { data: any; error: any };

    if (error) {
      console.error('Erro ao verificar primeiro usuário:', error);
      return false;
    }

    return data?.is_first_user ?? false;
  }
}

// Instância singleton do serviço de autenticação
export const supabaseAuthService = new SupabaseAuthService();
